import React, { useEffect, useState, useMemo } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import {
  Typography,
  Card,
  CardContent,
  Box,
  Stack,
  LinearProgress,
  linearProgressClasses,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";

const StyledText = styled("text")(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: theme.palette.text.secondary,
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <>
      <StyledText
        x={left + width / 2}
        y={primaryY}
        style={{
          fontSize: (theme) => theme.typography.h5.fontSize,
          fontWeight: (theme) => theme.typography.h5.fontWeight,
        }}
      >
        {primaryText}
      </StyledText>
      <StyledText
        x={left + width / 2}
        y={secondaryY}
        style={{
          fontSize: (theme) => theme.typography.body2.fontSize,
          fontWeight: (theme) => theme.typography.body2.fontWeight,
        }}
      >
        {secondaryText}
      </StyledText>
    </>
  );
}

const initialFeedbackTypes = [
  {
    label: "Positive",
    icon: "👍",
    key: "thumbs_up",
    color: "hsl(145, 60%, 50%)",
  },
  {
    label: "Negative",
    icon: "👎",
    key: "thumbs_down",
    color: "hsl(0, 65%, 55%)",
  },
  { label: "Neutral", icon: "—", key: "neutral", color: "hsl(220, 10%, 60%)" },
];

const ChartUserFeedback = ({ chatDetails }) => {
  const [feedbackData, setFeedbackData] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const processFeedbackData = (chats) => {
    const counts = {
      thumbs_up: 0,
      thumbs_down: 0,
      neutral: 0,
    };

    chats.forEach((chat) => {
      const feedback = chat.feedback?.toLowerCase();
      if (feedback === "thumbs_up") counts.thumbs_up += 1;
      else if (feedback === "thumbs_down") counts.thumbs_down += 1;
      else counts.neutral += 1; // treat null or undefined as neutral
    });

    const total = counts.thumbs_up + counts.thumbs_down + counts.neutral;

    const feedbackArray = initialFeedbackTypes.map((type) => {
      const value = counts[type.key];
      return {
        ...type,
        value,
        percentage: total ? Math.round((value / total) * 100) : 0,
      };
    });

    return feedbackArray;
  };

  useEffect(() => {
    if (chatDetails?.length) {
      const processedData = processFeedbackData(chatDetails);
      setFeedbackData(processedData);
    }
  }, [chatDetails]);

  const total = feedbackData.reduce((acc, item) => acc + item.value, 0);
  const colors = feedbackData.map((item) => item.color);
  const pieChartData = feedbackData.map((item) => ({
    label: item.label,
    value: item.value,
  }));

  // Filter and search feedback entries
  const filteredFeedback = useMemo(() => {
    if (!chatDetails?.length) return [];

    let filtered = chatDetails;

    // Filter by feedback type
    if (filterType !== "all") {
      if (filterType === "neutral") {
        filtered = filtered.filter(
          (chat) => !chat.feedback || chat.feedback === "neutral"
        );
      } else {
        filtered = filtered.filter((chat) => chat.feedback === filterType);
      }
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (chat) =>
          (chat.question && chat.question.toLowerCase().includes(term)) ||
          (chat.answer && chat.answer.toLowerCase().includes(term)) ||
          (chat.session?.user?.name &&
            chat.session.user.name.toLowerCase().includes(term)) ||
          (chat.session?.user?.email &&
            chat.session.user.email.toLowerCase().includes(term))
      );
    }

    // Sort by most recent first
    return filtered.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [chatDetails, filterType, searchTerm]);

  // Paginate results
  const paginatedFeedback = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredFeedback.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredFeedback, currentPage]);

  const totalPages = Math.ceil(filteredFeedback.length / itemsPerPage);

  // Reset pagination when filters change
  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleClose = () => {
    setDetailsOpen(false);
    setFilterType("all");
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 3,
          },
        }}
        onClick={() => setDetailsOpen(true)}
      >
        <CardContent>
          <Typography component="h2" variant="subtitle2" gutterBottom>
            User Feedback Overview
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PieChart
              colors={colors}
              margin={{ left: 80, right: 80, top: 80, bottom: 80 }}
              series={[
                {
                  data: pieChartData,
                  innerRadius: 75,
                  outerRadius: 100,
                  paddingAngle: 2,
                  highlightScope: { faded: "global", highlighted: "item" },
                },
              ]}
              height={260}
              width={260}
              slotProps={{ legend: { hidden: true } }}
            >
              {total > 0 && (
                <PieCenterLabel
                  primaryText={`${total}`}
                  secondaryText="Total Feedback"
                />
              )}
            </PieChart>
          </Box>

          {feedbackData.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              sx={{ alignItems: "center", gap: 2, pb: 2 }}
            >
              <Typography fontSize={24}>{item.icon}</Typography>
              <Stack sx={{ gap: 1, flexGrow: 1 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" fontWeight={500}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.value}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={item.percentage}
                  sx={{
                    height: 6,
                    borderRadius: 5,
                    [`& .${linearProgressClasses.bar}`]: {
                      backgroundColor: item.color,
                    },
                  }}
                />
              </Stack>
            </Stack>
          ))}
        </CardContent>
      </Card>

      {/* Feedback Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", pr: 2 }}
        >
          <Typography variant="h6" component="span">
            📊 User Feedback Details
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "#f9f9f9" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Summary Stats */}
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                📈 Feedback Summary
              </Typography>
              <Grid container spacing={2}>
                {feedbackData.map((item, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Typography fontSize={48} sx={{ mb: 1 }}>
                        {item.icon}
                      </Typography>
                      <Typography variant="h4" sx={{ mb: 1 }}>
                        {item.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.label} ({item.percentage}%)
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={item.percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          mt: 1,
                          [`& .${linearProgressClasses.bar}`]: {
                            backgroundColor: item.color,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Feedback Filtering and Search */}
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  💬 Feedback Details ({filteredFeedback.length} entries)
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search feedback..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 250 }}
                />
              </Box>

              {/* Filter Toggle Buttons */}
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={filterType}
                  exclusive
                  onChange={(event, newFilter) => {
                    if (newFilter !== null) {
                      handleFilterChange(newFilter);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="all">
                    All ({chatDetails?.length || 0})
                  </ToggleButton>
                  <ToggleButton value="thumbs_up">
                    👍 Positive (
                    {feedbackData.find((f) => f.key === "thumbs_up")?.value ||
                      0}
                    )
                  </ToggleButton>
                  <ToggleButton value="thumbs_down">
                    👎 Negative (
                    {feedbackData.find((f) => f.key === "thumbs_down")?.value ||
                      0}
                    )
                  </ToggleButton>
                  <ToggleButton value="neutral">
                    — Neutral (
                    {feedbackData.find((f) => f.key === "neutral")?.value || 0})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Feedback List */}
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {paginatedFeedback.map((chat, index) => (
                  <Paper
                    key={chat.id || index}
                    elevation={1}
                    sx={{ p: 2, mb: 2, borderRadius: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Chip
                        icon={
                          <span>
                            {
                              feedbackData.find(
                                (f) => f.key === (chat.feedback || "neutral")
                              )?.icon
                            }
                          </span>
                        }
                        label={
                          feedbackData.find(
                            (f) => f.key === (chat.feedback || "neutral")
                          )?.label
                        }
                        color={
                          chat.feedback === "thumbs_up"
                            ? "success"
                            : chat.feedback === "thumbs_down"
                            ? "error"
                            : "default"
                        }
                        variant="outlined"
                        size="small"
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>User:</strong>{" "}
                          {chat.session?.user?.name || "Anonymous"} (
                          {chat.session?.user?.email || "No email"})
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Question:</strong> {chat.question}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Answer:</strong>{" "}
                          {chat.answer || "No answer yet"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(
                            new Date(chat.createdAt),
                            "MMM dd, yyyy HH:mm"
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, page) => setCurrentPage(page)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}

                {/* Empty States */}
                {filteredFeedback.length === 0 && searchTerm && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <SearchIcon
                      sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No feedback found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search terms or filters
                    </Typography>
                  </Box>
                )}

                {filteredFeedback.length === 0 &&
                  !searchTerm &&
                  filterType !== "all" && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        No{" "}
                        {filterType === "neutral"
                          ? "neutral"
                          : filterType === "thumbs_up"
                          ? "positive"
                          : "negative"}{" "}
                        feedback found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try selecting a different filter or "All" to see all
                        feedback
                      </Typography>
                    </Box>
                  )}

                {chatDetails?.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No feedback available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      User feedback will appear here once users start rating
                      responses
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChartUserFeedback;
